import * as React from 'react';
import {useTheme} from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import {ListChildComponentProps, VariableSizeList, VariableSizeListProps} from 'react-window';

const CATEGORY_INDEX = 0;

export const ITEM_DEFAULT_SIZE = 40;
const ITEM_DOUBLE_LINES_SIZE = 60;

const ITEMS_COUNT_DEFAULT = 10;
const ITEMS_COUNT_DOUBLE_LINES = 6;

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
    const outerProps = React.useContext(OuterElementContext);

    return <div ref={ref} {...props} {...outerProps} />;
});

const renderRow = ({data, index, style}: ListChildComponentProps) =>
    React.cloneElement(data[index], {
        style
    });

function useResetCache(data: any) {
    const ref = React.useRef<VariableSizeList>(null);

    React.useEffect(() => {
        if (ref.current !== null) {
            ref.current.resetAfterIndex(0, true);
        }
    }, [data]);

    return ref;
}

export interface VirtualizedListboxProps {
    VariableSizeListProps?: Partial<VariableSizeListProps>;
}

export const VirtualizedListbox = React.forwardRef<HTMLDivElement, VirtualizedListboxProps>(
    ({children, VariableSizeListProps, ...rest}: VirtualizedListboxProps, ref) => {
        const theme = useTheme();
        const isUpSMBreakpoint = useMediaQuery(theme.breakpoints.up('sm'));
        const itemData = React.Children.toArray(children);
        const itemCount = itemData.length;

        const visibleItems = isUpSMBreakpoint ? ITEMS_COUNT_DEFAULT : ITEMS_COUNT_DOUBLE_LINES;
        const visibleItemsCount = itemCount < visibleItems ? itemCount : visibleItems;
        const itemSize = isUpSMBreakpoint ? ITEM_DEFAULT_SIZE : ITEM_DOUBLE_LINES_SIZE;

        const gridRef = useResetCache(itemCount);

        return (
            <div ref={ref}>
                <OuterElementContext.Provider value={rest}>
                    <VariableSizeList
                        itemData={itemData}
                        width="100%"
                        ref={gridRef}
                        height={itemSize * visibleItemsCount}
                        itemSize={index =>
                            index === CATEGORY_INDEX ? ITEM_DEFAULT_SIZE : itemSize
                        }
                        outerElementType={OuterElementType}
                        innerElementType="ul"
                        overscanCount={5}
                        itemCount={itemCount}
                        {...VariableSizeListProps}>
                        {renderRow}
                    </VariableSizeList>
                </OuterElementContext.Provider>
            </div>
        );
    }
);
